import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { servicenowCmdbApi, type CMDBSyncItem, type ServiceNowConfig } from '../api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { RefreshCw, Settings, Plus, ArrowUpDown, EyeOff, AlertTriangle, Database } from 'lucide-react'
import { SetupGuideDialog } from '../components/SetupGuideDialog'
import { RelationshipsTab } from '../components/RelationshipsTab'

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    new: { label: 'New', variant: 'default' },
    changed: { label: 'Changed', variant: 'secondary' },
    deleted: { label: 'Deleted', variant: 'destructive' },
    synced: { label: 'Synced', variant: 'outline' },
    ignored: { label: 'Ignored', variant: 'outline' },
}

export function ServiceNowCMDBPage() {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [showConfig, setShowConfig] = useState(false)
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [page, setPage] = useState(1)

    // Config
    const { data: configRes } = useQuery({
        queryKey: ['servicenow-config'],
        queryFn: async () => (await servicenowCmdbApi.getConfig()).data?.data,
    })
    const config = configRes

    // Config form state
    const [formConfig, setFormConfig] = useState<Partial<ServiceNowConfig>>({})
    const configToEdit = { ...config, ...formConfig }

    const saveConfigMutation = useMutation({
        mutationFn: (data: Partial<ServiceNowConfig>) => servicenowCmdbApi.saveConfig(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['servicenow-config'] })
            toast({ title: 'Saved', description: 'ServiceNow configuration saved.' })
            setShowConfig(false)
            setFormConfig({})
        },
        onError: (err: any) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
    })

    // Stats
    const { data: statsRes } = useQuery({
        queryKey: ['cmdb-stats'],
        queryFn: async () => (await servicenowCmdbApi.getStats()).data?.data,
    })
    const stats = statsRes

    // Items
    const { data: itemsRes, isLoading: itemsLoading } = useQuery({
        queryKey: ['cmdb-items', statusFilter, page],
        queryFn: async () => {
            const res = await servicenowCmdbApi.getItems({ status: statusFilter, page, limit: 50 })
            return res.data
        },
    })

    // Sync log
    const { data: syncLogRes } = useQuery({
        queryKey: ['cmdb-sync-log'],
        queryFn: async () => (await servicenowCmdbApi.getSyncLog()).data?.data,
    })
    const lastSync = syncLogRes?.[0]

    // Sync trigger
    const syncMutation = useMutation({
        mutationFn: () => servicenowCmdbApi.triggerSync(),
        onSuccess: () => {
            toast({ title: 'Sync started', description: 'Fetching data from ServiceNow CMDB...' })
            // Poll for completion
            const poll = setInterval(async () => {
                await queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
                await queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
                await queryClient.invalidateQueries({ queryKey: ['cmdb-sync-log'] })
                const log = await servicenowCmdbApi.getSyncLog()
                const latest = log.data?.data?.[0]
                if (latest?.status !== 'running') {
                    clearInterval(poll)
                    if (latest?.status === 'completed') {
                        toast({ title: 'Sync complete', description: `${latest.totalCIs} CIs processed: ${latest.newItems} new, ${latest.changedItems} changed, ${latest.deletedItems} deleted` })
                    } else {
                        toast({ title: 'Sync failed', description: latest?.errorMessage || 'Unknown error', variant: 'destructive' })
                    }
                }
            }, 3000)
        },
        onError: (err: any) => toast({ title: 'Sync failed', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    // Item actions
    const addMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.addItem(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
            toast({ title: 'Added', description: 'Object created in Vault Sentinel.' })
        },
        onError: (err: any) => toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    const updateMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.updateItem(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
            toast({ title: 'Updated', description: 'Object updated from CMDB changes.' })
        },
        onError: (err: any) => toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' }),
    })

    const ignoreMutation = useMutation({
        mutationFn: (id: number) => servicenowCmdbApi.ignoreItem(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cmdb-items'] })
            queryClient.invalidateQueries({ queryKey: ['cmdb-stats'] })
        },
    })

    const items = itemsRes?.data ?? []
    const totalItems = itemsRes?.total ?? 0

    return (
        <div className="space-y-6" style={{ padding: 'var(--spacing-page)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">ServiceNow CMDB</h1>
                    <p className="text-muted-foreground">Sync configuration items from ServiceNow CMDB into Vault Sentinel</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)}>
                        <Settings className="h-4 w-4 mr-1.5" />
                        Configure
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending || !config?.enabled}
                    >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                        Sync Now
                    </Button>
                </div>
            </div>

            {/* Config panel */}
            {showConfig && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">ServiceNow Connection</CardTitle>
                        <CardDescription>Configure the ServiceNow instance and CMDB table to sync from.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Instance URL</Label>
                                <Input
                                    placeholder="https://your-instance.service-now.com"
                                    value={configToEdit.instanceUrl || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, instanceUrl: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>CMDB Table</Label>
                                <Input
                                    placeholder="cmdb_ci"
                                    value={configToEdit.tableName || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, tableName: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Username</Label>
                                <Input
                                    value={configToEdit.username || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, username: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Password / API Key</Label>
                                <Input
                                    type="password"
                                    value={configToEdit.apiKey || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, apiKey: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Vault Sentinel Object ID Field</Label>
                                <Input
                                    placeholder="u_vault_sentinel_id"
                                    value={configToEdit.customFieldVaultSentinel || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, customFieldVaultSentinel: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>MVC Tag Field</Label>
                                <Input
                                    placeholder="u_mvc"
                                    value={configToEdit.customFieldMvc || ''}
                                    onChange={(e) => setFormConfig(p => ({ ...p, customFieldMvc: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-4 pt-2 pb-2">
                            <Label className="text-sm whitespace-nowrap">Auto-sync every</Label>
                            <Input
                                type="number"
                                min="0"
                                className="w-20"
                                value={configToEdit.syncInterval ?? 0}
                                onChange={(e) => setFormConfig(p => ({ ...p, syncInterval: parseInt(e.target.value, 10) || 0 }))}
                            />
                            <select
                                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                                value={configToEdit.syncIntervalUnit || 'hours'}
                                onChange={(e) => setFormConfig(p => ({ ...p, syncIntervalUnit: e.target.value as 'minutes' | 'hours' | 'days' }))}
                            >
                                <option value="minutes">minutes</option>
                                <option value="hours">hours</option>
                                <option value="days">days</option>
                            </select>
                            <span className="text-xs text-muted-foreground">Set to 0 to disable auto-sync</span>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={!!configToEdit.enabled}
                                        onCheckedChange={(v) => setFormConfig(p => ({ ...p, enabled: v }))}
                                    />
                                    <Label>Integration enabled</Label>
                                </div>
                                <SetupGuideDialog />
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => { setShowConfig(false); setFormConfig({}) }}>Cancel</Button>
                                <Button onClick={() => saveConfigMutation.mutate(configToEdit)} disabled={saveConfigMutation.isPending}>
                                    Save Configuration
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Tabs defaultValue="items">
                <TabsList>
                    <TabsTrigger value="items">Configuration Items</TabsTrigger>
                    <TabsTrigger value="relationships">Relationships</TabsTrigger>
                </TabsList>

                <TabsContent value="items" className="mt-4 space-y-6">

            {/* Stats cards */}
            <div className="grid grid-cols-5 gap-4">
                {[
                    { label: 'New', value: stats?.new ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Changed', value: stats?.changed ?? 0, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Deleted', value: stats?.deleted ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
                    { label: 'Synced', value: stats?.synced ?? 0, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Total CIs', value: stats?.total ?? 0, color: 'text-foreground', bg: 'bg-muted/50' },
                ].map((s) => (
                    <Card key={s.label} className={`${s.bg} border-0 cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => setStatusFilter(s.label === 'Total CIs' ? 'all' : s.label.toLowerCase())}>
                        <CardContent className="p-4">
                            <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
                            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Sync progress banner */}
            {(syncMutation.isPending || lastSync?.status === 'running') && (
                <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <RefreshCw className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-blue-800">Syncing with ServiceNow CMDB...</p>
                                <p className="text-xs text-blue-600 mt-0.5">Fetching configuration items from ServiceNow CMDB. This may take a moment for large CMDBs.</p>
                                <div className="mt-2 h-1.5 w-full bg-blue-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{ width: '60%' }} />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Last sync info */}
            {lastSync && lastSync.status !== 'running' && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Database className="h-4 w-4" />
                    Last sync: {lastSync.status === 'completed' ? (
                        <span>{new Date(lastSync.completedAt!).toLocaleString('en-GB')} — {lastSync.totalCIs} CIs, {lastSync.newItems} new, {lastSync.changedItems} changed, {lastSync.deletedItems} deleted</span>
                    ) : (
                        <span className="text-red-600">Failed: {lastSync.errorMessage}</span>
                    )}
                </div>
            )}

            {/* Filter tabs — segmented toggle matching Card/Table pattern */}
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/30 gap-1">
                {['all', 'new', 'changed', 'deleted', 'synced', 'ignored'].map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => { setStatusFilter(s); setPage(1) }}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            statusFilter === s
                                ? 'bg-background shadow-sm text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                ))}
            </div>

            {/* Items table */}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[80px]">Status</TableHead>
                            <TableHead>Sys ID</TableHead>
                            <TableHead>CI Name</TableHead>
                            <TableHead>Class</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Operational Status</TableHead>
                            <TableHead>VS Object ID</TableHead>
                            <TableHead className="w-[140px]">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {itemsLoading ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                    {config?.enabled ? 'No items. Click "Sync Now" to fetch from ServiceNow.' : 'Configure and enable the ServiceNow integration to get started.'}
                                </TableCell>
                            </TableRow>
                        ) : items.map((item: CMDBSyncItem) => {
                            const badge = STATUS_BADGES[item.syncStatus] || STATUS_BADGES.new
                            return (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Badge variant={badge.variant} className="text-xs">{badge.label}</Badge>
                                    </TableCell>
                                    <TableCell className="text-xs font-mono text-muted-foreground max-w-[120px] truncate" title={item.sysId}>{item.sysId?.substring(0, 12)}...</TableCell>
                                    <TableCell className="font-medium">{item.ciName || '—'}</TableCell>
                                    <TableCell className="text-muted-foreground text-xs">{item.ciClass || '—'}</TableCell>
                                    <TableCell className="text-xs">{item.ciLocation || '—'}</TableCell>
                                    <TableCell className="text-xs">{item.ciOperationalStatus || '—'}</TableCell>
                                    <TableCell className="text-xs font-mono">{item.vaultSentinelObjectId || '—'}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {item.syncStatus === 'new' && (
                                                <>
                                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addMutation.mutate(item.id)}>
                                                        <Plus className="h-3 w-3 mr-1" /> Add
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => ignoreMutation.mutate(item.id)}>
                                                        <EyeOff className="h-3 w-3" />
                                                    </Button>
                                                </>
                                            )}
                                            {item.syncStatus === 'changed' && (
                                                <>
                                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateMutation.mutate(item.id)} title={item.changeDetails || ''}>
                                                        <ArrowUpDown className="h-3 w-3 mr-1" /> Apply
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => ignoreMutation.mutate(item.id)}>
                                                        <EyeOff className="h-3 w-3" />
                                                    </Button>
                                                </>
                                            )}
                                            {item.syncStatus === 'deleted' && (
                                                <span className="text-xs text-muted-foreground">
                                                    <AlertTriangle className="h-3 w-3 inline mr-1" /> Removed from CMDB
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {totalItems > 50 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, totalItems)} of {totalItems}</span>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                        <Button variant="outline" size="sm" disabled={page * 50 >= totalItems} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </div>
                </div>
            )}

                </TabsContent>

                <TabsContent value="relationships" className="mt-4">
                    <RelationshipsTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}
